import React from "react";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import type { SpecsValidationIssue } from "../../service/SpecsRevisionService";
import {
  filterDisplayedIssues,
  getSpecsIssueSeverity,
  summarizeSpecsValidationIssues,
} from "./specsValidationIssues";
import type { SpecsEditorValidationResult } from "./SpecsValidationDialog";

function formatIssueCount(issues: SpecsValidationIssue[]) {
  const errorCount = issues.filter(
    (issue) => getSpecsIssueSeverity(issue) === "error"
  ).length;
  const warningCount = issues.filter(
    (issue) => getSpecsIssueSeverity(issue) === "warning"
  ).length;

  if (errorCount > 0 && warningCount > 0) {
    return `${errorCount} error${errorCount === 1 ? "" : "s"} and ${warningCount} warning${warningCount === 1 ? "" : "s"}`;
  }

  if (errorCount > 0) {
    return `${errorCount} error${errorCount === 1 ? "" : "s"}`;
  }

  return `${warningCount} warning${warningCount === 1 ? "" : "s"}`;
}

export default function SpecsValidationIssueSummary({
  validationResult,
  onOpenDetails,
}: {
  validationResult: SpecsEditorValidationResult | null;
  onOpenDetails?: () => void;
}) {
  const issues = filterDisplayedIssues(validationResult?.issues ?? []);
  if (validationResult?.valid || issues.length === 0) {
    return null;
  }

  const { summaries, hiddenCount } = summarizeSpecsValidationIssues(issues);
  const severity = issues.some(
    (issue) => getSpecsIssueSeverity(issue) === "error"
  )
    ? "error"
    : "warning";

  return (
    <Alert
      severity={severity}
      action={
        onOpenDetails ? (
          <Button color="inherit" size="small" onClick={onOpenDetails}>
            Details
          </Button>
        ) : undefined
      }
      sx={{ alignItems: "flex-start" }}
    >
      <Stack spacing={1}>
        <Typography variant="body2">
          Validation found {formatIssueCount(issues)}. Fix the highlighted
          fields or listed paths before saving or publishing.
        </Typography>
        <Stack spacing={0.75}>
          {summaries.map((summary, index) => (
            <Box key={`${summary.location}-${summary.message}-${index}`}>
              <Typography
                component="span"
                sx={{
                  fontFamily: "monospace",
                  fontSize: 12,
                  fontWeight: 600,
                  mr: 0.75,
                }}
              >
                {summary.location}
              </Typography>
              <Typography component="span" variant="body2">
                {summary.message}
              </Typography>
            </Box>
          ))}
          {hiddenCount > 0 ? (
            <Typography variant="body2" color="text.secondary">
              {hiddenCount} more issue{hiddenCount === 1 ? "" : "s"} in the
              details.
            </Typography>
          ) : null}
        </Stack>
      </Stack>
    </Alert>
  );
}
